import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getAdminArticle,
  createArticle,
  updateArticle,
} from '../api/articlesApi';
import { useAuth } from '../contexts/AuthContext';
import RichTextEditor from '../components/RichTextEditor';

const emptyForm = {
  slug: '',
  status: 'DRAFT',
  coverPhotoUrl: '',
  translations: {
    RU: {
      title: '',
      summary: '',
      content: '',
    },
    EN: {
      title: '',
      summary: '',
      content: '',
    },
  },
};

function getRoles(currentUser) {
  const rawRoles = currentUser?.roles || currentUser?.authorities || [];

  return rawRoles.map((role) => {
    if (typeof role === 'string') return role;
    return role.authority || role.name || '';
  });
}

function slugify(value) {
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/ё/g, 'e')
    .replace(/й/g, 'i')
    .replace(/ц/g, 'ts')
    .replace(/у/g, 'u')
    .replace(/к/g, 'k')
    .replace(/е/g, 'e')
    .replace(/н/g, 'n')
    .replace(/г/g, 'g')
    .replace(/ш/g, 'sh')
    .replace(/щ/g, 'sch')
    .replace(/з/g, 'z')
    .replace(/х/g, 'h')
    .replace(/ъ/g, '')
    .replace(/ф/g, 'f')
    .replace(/ы/g, 'y')
    .replace(/в/g, 'v')
    .replace(/а/g, 'a')
    .replace(/п/g, 'p')
    .replace(/р/g, 'r')
    .replace(/о/g, 'o')
    .replace(/л/g, 'l')
    .replace(/д/g, 'd')
    .replace(/ж/g, 'zh')
    .replace(/э/g, 'e')
    .replace(/я/g, 'ya')
    .replace(/ч/g, 'ch')
    .replace(/с/g, 's')
    .replace(/м/g, 'm')
    .replace(/и/g, 'i')
    .replace(/т/g, 't')
    .replace(/ь/g, '')
    .replace(/б/g, 'b')
    .replace(/ю/g, 'yu')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function ArticleEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const roles = getRoles(currentUser);
  const isAdmin = roles.includes('ROLE_ADMIN') || roles.includes('ADMIN');

  const isEditMode = Boolean(id);

  const [form, setForm] = useState(emptyForm);
  const [activeLang, setActiveLang] = useState('RU');
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const uploadHost =
    process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';

  useEffect(() => {
    if (!isEditMode) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const { data } = await getAdminArticle(id);

        if (!mounted) return;

        const nextForm = {
          slug: data.slug || '',
          status: data.status || 'DRAFT',
          coverPhotoUrl: data.coverPhotoUrl || '',
          translations: {
            RU: {
              title: '',
              summary: '',
              content: '',
            },
            EN: {
              title: '',
              summary: '',
              content: '',
            },
          },
        };

        if (Array.isArray(data.translations)) {
          data.translations.forEach((translation) => {
            if (translation.lang === 'RU' || translation.lang === 'EN') {
              nextForm.translations[translation.lang] = {
                title: translation.title || '',
                summary: translation.summary || '',
                content: translation.content || '',
              };
            }
          });
        }

        setForm(nextForm);
      } catch (err) {
        console.error(err);
        setError('Не удалось загрузить статью');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [id, isEditMode]);

  const handleMainChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateTranslation = (lang, field, value) => {
    setForm((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value,
        },
      },
    }));
  };

  const handleGenerateSlug = () => {
    const source =
      form.translations.RU.title ||
      form.translations.EN.title ||
      '';

    setForm((prev) => ({
      ...prev,
      slug: slugify(source),
    }));
  };

  const validate = () => {
    if (!form.slug.trim()) {
      return 'Укажи slug';
    }

    const filledTranslations = Object.entries(form.translations).filter(
      ([, translation]) =>
        translation.title.trim() ||
        translation.summary.trim() ||
        translation.content.trim()
    );

    if (filledTranslations.length === 0) {
      return 'Заполни хотя бы одну языковую версию статьи';
    }

    for (const [lang, translation] of filledTranslations) {
      if (!translation.title.trim()) {
        return `Укажи заголовок для ${lang}`;
      }

      if (!translation.content.trim()) {
        return `Укажи текст статьи для ${lang}`;
      }
    }

    return '';
  };

  const buildPayload = () => {
    const translations = Object.entries(form.translations)
      .filter(([, translation]) => {
        return (
          translation.title.trim() ||
          translation.summary.trim() ||
          translation.content.trim()
        );
      })
      .map(([lang, translation]) => ({
        lang,
        title: translation.title.trim(),
        summary: translation.summary.trim(),
        content: translation.content,
      }));

    return {
      slug: form.slug.trim(),
      status: form.status,
      coverPhotoUrl: form.coverPhotoUrl.trim() || null,
      translations,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Article form submit clicked');

    const validationError = validate();

    if (validationError) {
      setError(validationError);
      console.warn('Article validation stopped submit:', validationError);
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = buildPayload();

      console.log('Article payload:', payload);

      const { data } = isEditMode
        ? await updateArticle(id, payload)
        : await createArticle(payload);

      const savedSlug = data?.slug || payload.slug;

      navigate(`/articles/${savedSlug}`);
    } catch (err) {
      console.error('Ошибка при сохранении статьи:', err);
      setError('Не удалось сохранить статью');
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        Проверка прав…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl mb-4">Нет доступа</h1>
          <p className="text-gray-300">
            Создание и редактирование статей доступно только администратору.
          </p>

          <Link to="/articles" className="mt-4 inline-block text-blue-400 hover:underline">
            ← К списку статей
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        Загрузка…
      </div>
    );
  }

  const coverPreviewUrl = form.coverPhotoUrl
    ? form.coverPhotoUrl.startsWith('/')
      ? `${uploadHost}${form.coverPhotoUrl}`
      : form.coverPhotoUrl
    : null;

  return (
    <form onSubmit={handleSubmit} className="min-h-screen bg-gray-900 text-white">
      <div className="p-6 max-w-3xl mx-auto">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl mb-1">
              {isEditMode ? 'Редактировать статью' : 'Новая статья'}
            </h1>

            <Link to="/articles" className="text-blue-400 hover:underline text-sm">
              ← К списку статей
            </Link>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 rounded"
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded border border-red-700 bg-red-950/40 p-3 text-red-300">
            {error}
          </div>
        )}

        <label className="block mb-2">Slug</label>
        <div className="flex gap-2 mb-4">
          <input
            name="slug"
            value={form.slug}
            onChange={handleMainChange}
            className="w-full p-2 bg-gray-800 rounded border border-gray-700"
            placeholder="early-maxim-m1905"
          />

          <button
            type="button"
            onClick={handleGenerateSlug}
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded"
          >
            Сгенерировать
          </button>
        </div>

        <label className="block mb-2">Статус</label>
        <select
          name="status"
          value={form.status}
          onChange={handleMainChange}
          className="w-full mb-4 p-2 bg-gray-800 rounded border border-gray-700"
        >
          <option value="DRAFT">Черновик</option>
          <option value="PUBLISHED">Опубликовано</option>
        </select>

        <label className="block mb-2">URL обложки</label>
        <input
          name="coverPhotoUrl"
          value={form.coverPhotoUrl}
          onChange={handleMainChange}
          className="w-full mb-4 p-2 bg-gray-800 rounded border border-gray-700"
          placeholder="/uploads/articles/cover.jpg"
        />

        {coverPreviewUrl && (
          <div className="mb-6">
            <label className="block mb-2">Превью обложки</label>
            <img
              src={coverPreviewUrl}
              alt=""
              className="max-h-72 rounded border border-gray-700 object-cover"
            />
          </div>
        )}

        <div className="mb-4">
          <label className="block mb-2">Язык статьи</label>

          <div className="flex rounded border border-gray-700 overflow-hidden w-fit">
            <button
              type="button"
              onClick={() => setActiveLang('RU')}
              className={`px-4 py-2 text-sm ${
                activeLang === 'RU'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              RUS
            </button>

            <button
              type="button"
              onClick={() => setActiveLang('EN')}
              className={`px-4 py-2 text-sm ${
                activeLang === 'EN'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              ENG
            </button>
          </div>
        </div>

        <label className="block mb-2">Заголовок {activeLang}</label>
        <input
          value={form.translations[activeLang].title}
          onChange={(e) =>
            updateTranslation(activeLang, 'title', e.target.value)
          }
          className="w-full mb-4 p-2 bg-gray-800 rounded border border-gray-700"
          placeholder="Заголовок статьи"
        />

        <label className="block mb-2">Краткое описание {activeLang}</label>
        <textarea
          value={form.translations[activeLang].summary}
          onChange={(e) =>
            updateTranslation(activeLang, 'summary', e.target.value)
          }
          rows={3}
          className="w-full mb-4 p-2 bg-gray-800 rounded border border-gray-700"
          placeholder="Краткое описание для списка статей"
        />

        <div className="mb-6">
          <label className="block mb-2">Текст статьи {activeLang}</label>

          <div className={activeLang === 'RU' ? 'block' : 'hidden'}>
            <RichTextEditor
              value={form.translations.RU.content || ''}
              onChange={(val) => updateTranslation('RU', 'content', val)}
              placeholder="Введите русский текст статьи"
            />
          </div>

          <div className={activeLang === 'EN' ? 'block' : 'hidden'}>
            <RichTextEditor
              value={form.translations.EN.content || ''}
              onChange={(val) => updateTranslation('EN', 'content', val)}
              placeholder="Enter English article text"
            />
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-xl mb-3">Предпросмотр {activeLang}</h2>

          <div className="rounded border border-gray-700 bg-gray-800 p-4">
            <h3 className="text-2xl font-bold mb-2">
              {form.translations[activeLang].title || 'Без заголовка'}
            </h3>

            {form.translations[activeLang].summary && (
              <p className="text-gray-300 italic mb-4 whitespace-pre-wrap">
                {form.translations[activeLang].summary}
              </p>
            )}

            <div
              className="prose prose-invert max-w-none text-gray-200"
              dangerouslySetInnerHTML={{
                __html: form.translations[activeLang].content || '',
              }}
            />
          </div>
        </div>

        <div className="mt-6 flex gap-2 justify-between">
          <Link
            to="/articles"
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded"
          >
            Отмена
          </Link>

          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 rounded"
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </form>
  );
}